import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 10000, // 10 秒超時
});

// CHANGED: Add simple in-memory cache for machines data
const machinesCache = {
  data: null,
  timestamp: null,
  TTL: 5 * 60 * 1000, // 5 分鐘緩存
};

// CHANGED: Track ongoing request to prevent duplicate calls
let machinesRequestPromise = null;

const getCachedMachines = () => {
  if (machinesCache.data && machinesCache.timestamp) {
    const age = Date.now() - machinesCache.timestamp;
    if (age < machinesCache.TTL) {
      return machinesCache.data;
    }
  }
  return null;
};

const setCachedMachines = (data) => {
  machinesCache.data = data;
  machinesCache.timestamp = Date.now();
};

const TOKEN_STORAGE_KEY = 'ponponAuthToken';
let cachedAuthToken = null;

const resolveSessionStorage = () => {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    return window.sessionStorage || null;
  } catch (error) {
    return null;
  }
};

const tokenStorage = resolveSessionStorage();
if (tokenStorage) {
  const storedToken = tokenStorage.getItem(TOKEN_STORAGE_KEY);
  if (storedToken) {
    api.defaults.headers.common.Authorization = `Bearer ${storedToken}`;
    cachedAuthToken = storedToken;
  }
}

export const setAuthToken = (token) => {
  if (token) {
    if (cachedAuthToken !== token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
      cachedAuthToken = token;
    }
    if (tokenStorage) {
      tokenStorage.setItem(TOKEN_STORAGE_KEY, token);
    }
  } else {
    delete api.defaults.headers.common.Authorization;
    cachedAuthToken = null;
    if (tokenStorage) {
      tokenStorage.removeItem(TOKEN_STORAGE_KEY);
    }
  }
};

// Machines API
// CHANGED: Add caching and request deduplication to prevent duplicate API calls
export const getMachines = async (useCache = true) => {
  // Check cache first
  if (useCache) {
    const cached = getCachedMachines();
    if (cached) {
      return cached;
    }
  }

  // CHANGED: If there's already a request in progress, return the same Promise
  // This prevents duplicate requests when multiple components call getMachines simultaneously
  if (machinesRequestPromise) {
    console.log('[getMachines] Reusing existing request');
    return machinesRequestPromise;
  }

  // Create new request Promise
  machinesRequestPromise = (async () => {
    try {
      console.log('[getMachines] Starting new request');
      const response = await api.get('/machines');
      const data = response.data;
      // Cache the result
      setCachedMachines(data);
      machinesRequestPromise = null; // Clear Promise after success
      return data;
    } catch (error) {
      console.error('Error fetching machines:', error);
      machinesRequestPromise = null; // Clear Promise after error
      
      // If request fails but we have cached data, return cached data
      const cached = getCachedMachines();
      if (cached) {
        console.warn('Using cached machines data due to API error');
        return cached;
      }
      throw error;
    }
  })();

  return machinesRequestPromise;
};

// Spin gacha API
export const spinGacha = async ({ userId = null, machineId, tokenCost = null } = {}) => {
  if (!machineId || typeof machineId !== 'string') {
    throw new Error('machineId is required to spin the machine');
  }
  try {
    const payload = { userId, machineId };
    if (typeof tokenCost === 'number') {
      payload.tokenCost = tokenCost;
    }
    const response = await api.post('/spin', payload);
    return response.data;
  } catch (error) {
    console.error('Error spinning gacha:', error);
    throw error;
  }
};

// Firebase-authenticated user bootstrap
export const syncUserAccount = async ({ displayName, email, photoURL } = {}) => {
  try {
    const payload = {};
    if (displayName) {
      payload.displayName = displayName;
    }
    if (email) {
      payload.email = email;
    }
    if (photoURL) {
      payload.photoURL = photoURL;
    }

    const response = await api.post('/auth/session', payload);
    return response.data;
  } catch (error) {
    console.error('Error syncing user account:', error);
    throw error;
  }
};

// Purchase tokens via Stripe checkout
export const createCheckoutSession = async ({ bundleId, tokens } = {}) => {
  const payload = {};
  if (bundleId) {
    payload.bundleId = bundleId;
  } else if (tokens) {
    payload.tokens = tokens;
  }
  try {
    const response = await api.post('/tokens/checkout', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating checkout session:', error);
    throw error;
  }
};

export const getCheckoutSessionStatus = async (sessionId) => {
  if (!sessionId || typeof sessionId !== 'string') {
    throw new Error('sessionId is required');
  }
  try {
    const response = await api.get(`/tokens/checkout-session/${sessionId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { status: 'pending' };
    }
    console.error('Error retrieving checkout session:', error);
    throw error;
  }
};

export const createLockerExpansionSession = async () => {
  try {
    const response = await api.post('/locker/expand/checkout');
    return response.data;
  } catch (error) {
    console.error('Error creating locker expansion session:', error);
    throw error;
  }
};

export const getLockerExpansionSessionStatus = async (sessionId) => {
  if (!sessionId || typeof sessionId !== 'string') {
    throw new Error('sessionId is required');
  }
  try {
    const response = await api.get(`/locker/expansion-session/${sessionId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { status: 'pending' };
    }
    console.error('Error retrieving locker expansion session:', error);
    throw error;
  }
};

export const redeemCollectionReward = async (collectionId) => {
  if (!collectionId || typeof collectionId !== 'string') {
    throw new Error('collectionId is required');
  }
  const response = await api.post(
    `/collections/${encodeURIComponent(collectionId)}/redeem`,
  );
  return response.data;
};

export const shipLockerContents = async (payload = {}) => {
  try {
    const response = await api.post('/locker/ship', payload);
    return response.data;
  } catch (error) {
    console.error('Error shipping locker contents:', error);
    throw error;
  }
};

export const createShippingCheckout = async (payload = {}) => {
  try {
    const response = await api.post('/locker/ship/checkout', payload);
    return response.data;
  } catch (error) {
    console.error('Error creating shipping checkout session:', error);
    throw error;
  }
};

export const getShippingCheckoutStatus = async (sessionId) => {
  if (!sessionId || typeof sessionId !== 'string') {
    throw new Error('sessionId is required');
  }
  try {
    const response = await api.get(`/locker/ship/checkout-session/${sessionId}`);
    return response.data;
  } catch (error) {
    if (error.response?.status === 404) {
      return { status: 'pending' };
    }
    console.error('Error retrieving shipping checkout session:', error);
    throw error;
  }
};

// Get user info API
export default api;
